
import { useState, useEffect } from "react";
import { z } from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useLocation, useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/components/ui/use-toast";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { AlertTriangle, CheckCircle } from "lucide-react";

const resetPasswordSchema = z
  .object({
    password: z.string().min(6, {
      message: "Password must be at least 6 characters",
    }),
    confirmPassword: z.string(),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: "Passwords do not match",
    path: ["confirmPassword"],
  });

type ResetPasswordValues = z.infer<typeof resetPasswordSchema>;

export function ResetPasswordForm() {
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [tokenVerified, setTokenVerified] = useState(true); // Assume valid until proven otherwise
  const { toast } = useToast();
  const location = useLocation();
  const navigate = useNavigate();

  const form = useForm<ResetPasswordValues>({
    resolver: zodResolver(resetPasswordSchema),
    defaultValues: {
      password: "",
      confirmPassword: "",
    },
  });

  // Check if we have a valid session from the recovery link
  useEffect(() => {
    const checkRecoveryToken = async () => {
      try {
        console.log("Checking recovery token...");
        const { data, error } = await supabase.auth.getSession();
        
        console.log("Session check:", data.session ? "Valid recovery session" : "No valid session");
        
        if (error || !data.session) {
          console.error("Recovery token validation error:", error);
          setTokenVerified(false);
          setError(error?.message || "Password reset link is invalid or has expired. Please request a new one.");
        }
      } catch (err: any) {
        console.error("Error checking recovery token:", err);
        setTokenVerified(false);
        setError("An error occurred while validating your reset link. Please try again.");
      }
    };
    
    checkRecoveryToken();
  }, []);

  async function onSubmit(values: ResetPasswordValues) {
    setIsLoading(true);
    setError(null);
    
    try {
      console.log("Submitting password reset...");
      const { error } = await supabase.auth.updateUser({
        password: values.password,
      });
      
      if (error) {
        console.error("Password update error:", error);
        throw error;
      }
      
      console.log("Password updated successfully");
      setIsSuccess(true);
      toast({
        title: "Password updated",
        description: "Your password has been successfully updated",
      });
    } catch (error: any) {
      console.error("Password reset error:", error);
      setError(error.message || "Failed to update password. Please try again.");
      toast({
        title: "Error",
        description: error.message || "Failed to update password",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  }

  const handleRequestNewLink = () => {
    navigate("/auth?mode=forgot-password");
  };

  return (
    <div className="space-y-6">
      {!tokenVerified ? (
        <div className="space-y-4">
          <Alert variant="destructive">
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
          <p className="text-center text-sm">
            The password reset link is invalid or has expired.
          </p>
          <Button className="w-full" onClick={handleRequestNewLink}>
            Request a new reset link
          </Button>
        </div>
      ) : isSuccess ? (
        <div className="space-y-4 text-center">
          <div className="mx-auto flex items-center justify-center w-12 h-12 rounded-full bg-green-100">
            <CheckCircle className="h-6 w-6 text-green-600" />
          </div>
          <h3 className="text-lg font-medium">Password Reset Successful</h3>
          <p className="text-sm text-muted-foreground">
            Your password has been updated. You can now sign in with your new password.
          </p>
          <Button className="mt-4 w-full" onClick={() => navigate("/auth")}>
            Go to sign in
          </Button>
        </div>
      ) : (
        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(onSubmit)}
            className="space-y-6"
          >
            <div className="space-y-2 text-center">
              <h3 className="text-lg font-medium">Reset your password</h3>
              <p className="text-sm text-muted-foreground">
                Enter a new password for your account
              </p>
            </div>
            
            {error && (
              <Alert variant="destructive">
                <AlertTriangle className="h-4 w-4" />
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}
            
            <FormField
              control={form.control}
              name="password"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>New Password</FormLabel>
                  <FormControl>
                    <Input
                      placeholder="New password"
                      type="password"
                      autoComplete="new-password"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            
            <FormField
              control={form.control}
              name="confirmPassword"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Confirm Password</FormLabel>
                  <FormControl>
                    <Input
                      placeholder="Confirm new password"
                      type="password"
                      autoComplete="new-password"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            
            <Button
              type="submit"
              className="w-full"
              disabled={isLoading}
            >
              {isLoading ? "Updating..." : "Reset Password"}
            </Button>
          </form>
        </Form>
      )}
    </div>
  );
}
